import { useCallback, useEffect, useState } from 'react'
import { gravarProjetor, modoInicial } from '../projetor'

/**
 * Liga e desliga o MODO PROJETOR, e o publica no `<html>`.
 *
 * O atributo vai em `document.documentElement` e não numa classe de componente
 * React de propósito: as comemorações são `position: fixed` e cobrem a tela
 * inteira, e o CSS delas precisa alcançar o perfil sem depender de onde elas
 * estão na árvore.
 *
 * A tecla `P` existe para a sala: trocar o endereço do telão com o evento
 * rodando é pedir para alguém fechar a aba errada. Mesmo cuidado dos outros
 * atalhos — não sequestra o teclado de quem está digitando num campo.
 */
export function usarModoProjetor(): { projetor: boolean; alternar: () => void } {
  const [projetor, setProjetor] = useState(() =>
    modoInicial(
      typeof location === 'undefined' ? '' : location.search,
      typeof localStorage === 'undefined' ? null : localStorage,
    ),
  )

  /* O atributo e o `localStorage` andam juntos: o primeiro é o que o CSS lê, o
     segundo é o que sobrevive ao F5 do telão. */
  useEffect(() => {
    const raiz = typeof document === 'undefined' ? null : document.documentElement
    if (raiz !== null) {
      if (projetor) raiz.setAttribute('data-projetor', '1')
      else raiz.removeAttribute('data-projetor')
    }
    gravarProjetor(typeof localStorage === 'undefined' ? null : localStorage, projetor)
  }, [projetor])

  const alternar = useCallback(() => setProjetor(ligado => !ligado), [])

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      const alvo = evento.target as HTMLElement | null
      if (alvo && /^(INPUT|TEXTAREA|SELECT)$/.test(alvo.tagName)) return
      /* `KeyP` é o código FÍSICO da tecla: funciona igual em teclado ABNT e US,
         e não dispara em acento morto. Com `Ctrl`/`Alt`/`Meta` fica de fora —
         `Ctrl+P` é imprimir, e o telão não pode roubar isso. */
      if (evento.code !== 'KeyP' || evento.ctrlKey || evento.altKey || evento.metaKey) return
      evento.preventDefault()
      alternar()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [alternar])

  return { projetor, alternar }
}
